(() => {
  'use strict';
  if (customElements.get('gc-direct-booking')) return;
  const SOURCE = 'https://memorial-protection-plan.lovable.app/booking';
  const ORIGIN = new URL(SOURCE).origin;
  const STEPS = new Set(['what_needs_doing', 'lettering', 'where', 'review']);

  class GCDirectBooking extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.frame = this.querySelector('iframe');
      this.status = this.querySelector('[data-booking-status]');
      this.loadState = this.querySelector('[data-booking-load-state]');
      this.fallbackLink = this.querySelector('[data-booking-fallback]');
      if (!this.frame) return;
      this.ready = false;
      this.lastStep = null;
      if (this.fallbackLink) this.fallbackLink.hidden = true;
      if (this.loadState) this.loadState.hidden = false;
      const { signal } = this.controller;
      window.addEventListener('message', event => this.receive(event), { signal });
      this.frame.addEventListener('load', () => this.sendContext(), { signal });
      this.frame.addEventListener('error', () => this.showFallback(), { signal });
      document.addEventListener('visitorConsentCollected', () => this.sendContext(), { signal });
      document.addEventListener('click', event => {
        const link = event.target.closest('a[data-booking-anchor]');
        if (!link || new URL(link.href, window.location.href).hash !== `#${this.id}`) return;
        event.preventDefault();
        this.scrollIntoView({ behavior: this.scrollBehavior(), block: 'start' });
        this.frame.focus({ preventScroll: true });
        window.history.replaceState(null, '', `#${this.id}`);
      }, { signal });
      this.timer = window.setTimeout(() => { if (!this.ready) this.showFallback(); }, 15000);
      // Listen first: this app reports readiness through gc-resize, not gc-ready.
      this.frame.src = SOURCE;
      if (window.Shopify?.loadFeatures && !window.Shopify.customerPrivacy) {
        window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.2' }], error => {
          if (!error && this.isConnected) this.sendContext();
        });
      }
    }

    scrollBehavior() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    }

    context() {
      const privacy = window.Shopify?.customerPrivacy;
      const analytics = Boolean(privacy && typeof privacy.analyticsProcessingAllowed === 'function' && privacy.analyticsProcessingAllowed());
      const marketing = Boolean(privacy && typeof privacy.marketingAllowed === 'function' && privacy.marketingAllowed());
      const query = new URLSearchParams(window.location.search);
      const keys = [];
      if (analytics) keys.push('utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term');
      if (marketing) keys.push('gclid', 'fbclid');
      const params = {};
      keys.forEach(key => {
        const value = query.get(key);
        if (value) params[key] = value.slice(0, 500);
      });
      if (analytics || marketing) params.landing_page = `${window.location.origin}${window.location.pathname}`;
      return params;
    }

    sendContext() {
      this.frame?.contentWindow?.postMessage({ type: 'gc-attribution', params: this.context() }, ORIGIN);
      // The child merges attribution into its own session storage. Empty params
      // stop new forwarding, but cannot clear identifiers previously stored there.
    }

    receive(event) {
      if (event.origin !== ORIGIN || event.source !== this.frame.contentWindow) return;
      const data = event.data;
      if (!data || typeof data !== 'object' || Array.isArray(data)) return;
      if (data.type === 'gc-resize' && Number.isFinite(data.height) && data.height >= 240 && data.height <= 14000) {
        this.frame.style.height = `${Math.ceil(data.height)}px`;
        if (!this.ready) {
          this.ready = true;
          this.dataset.ready = 'true';
          delete this.dataset.fallback;
          window.clearTimeout(this.timer);
          if (this.fallbackLink === document.activeElement) this.frame.focus({ preventScroll: true });
          if (this.fallbackLink) this.fallbackLink.hidden = true;
          if (this.loadState) this.loadState.hidden = true;
          if (this.status) this.status.textContent = '';
          this.sendContext();
        }
      }
      if (data.type === 'gc-step' && STEPS.has(data.step) && Number.isInteger(data.index) && data.index >= 0 && data.index <= 3) {
        const previous = this.lastStep;
        this.lastStep = data.step;
        if (previous && previous !== data.step) {
          window.requestAnimationFrame(() => {
            if (this.isConnected && this.lastStep === data.step) this.scrollIntoView({ behavior: 'auto', block: 'start' });
          });
        }
      }
      // No navigation, Lead, checkout or Purchase is inferred from messages.
      // The embedded app owns the user-initiated top-level Shopify checkout.
    }

    showFallback() {
      this.ready = false;
      delete this.dataset.ready;
      this.dataset.fallback = 'true';
      if (this.loadState) this.loadState.hidden = false;
      if (this.fallbackLink) this.fallbackLink.hidden = false;
      if (this.status) this.status.textContent = 'The booking builder hasn’t loaded. Open it in a new tab, or call 0330 043 0456 for help.';
    }

    disconnectedCallback() {
      window.clearTimeout(this.timer);
      this.controller?.abort();
      this.controller = null;
    }
  }
  customElements.define('gc-direct-booking', GCDirectBooking);
})();
